import { fileLoadingAtom, fileLoadingProgressAtom, initialMultiplayerSyncedAtom } from '@/app/atoms/fileLoadingAtom';
import { Progress } from '@/shared/shadcn/ui/progress';
import { useAtomValue } from 'jotai';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';

const SHOW_AFTER_MS = 500;
// Brief moment to flash the bar at 100% before unmounting once the load
// completes. Gives a satisfying "filled up" finish, and masks cases where
// Content-Length doesn't match the streamed byte count so the bar ends
// somewhere short of 100%.
const COMPLETION_FLASH_MS = 250;
// Minimum amount of time the overlay stays mounted once it has become
// visible. Guards against rapid `isLoading` true -> false -> true oscillations
// (e.g. router transition edges) that would otherwise unmount the overlay
// briefly and reveal the dashboard underneath.
const MIN_VISIBLE_MS = 800;
// Time-based gating for the progress bar. We don't know the user's
// connection speed up front, so a fixed file-size threshold either flickers
// for fast users or hides progress from slow users. Instead, we use the
// actual streamed throughput to decide.
//
// Don't even consider showing the bar until the fetch has been running for
// at least this long. Cached / fast loads finish inside this window without
// the bar ever appearing, and the bytes received during the window give us
// a real throughput sample (avoiding TCP slow-start noise).
const BAR_WARMUP_MS = 200;

// After the warmup, only show the bar if the projected remaining download
// time exceeds this threshold. Below it, the bar would flash through too
// quickly to read as useful progress; the GIF backdrop carries the loading
// signal in that window. The completion flash is gated on the bar having
// ever been shown, so it auto-disables when this never trips.
const BAR_MIN_ETA_MS = 400;

interface Props {
  /**
   * If true, skip the anti-flicker delay and show immediately. Use this when
   * the user has already been seeing the static index.html loading shell
   * (e.g. cold load via direct URL) so the React overlay takes over without
   * a visible gap.
   */
  immediate?: boolean;
}

/**
 * Full-screen Quadratic loading overlay shown while a file is being fetched
 * and deserialized. Includes a real progress bar fed by streamed chunks from
 * the core worker's file fetch (see `core.ts` `fetchGridFile`).
 *
 * Visibility is gated by a short delay so fast / cached loads don't flicker.
 *
 * The overlay activates as soon as react-router starts navigating to a file
 * route, so it covers the lazy-chunk download window before the route's
 * loader (which is what flips fileLoadingAtom to 'loading') even runs.
 *
 * The Quadratic logo (gray etching backdrop + animated boxes-filling-in gif)
 * is rendered by the static loading shell in `index.html` (see the
 * `[data-loading-start] body:after` rule) and painted *above* this React
 * overlay (z-index 10000 vs 9999). This component therefore only owns the
 * opaque background mask and the progress bar — the same gif element keeps
 * playing seamlessly across the cold-load → hydrated handoff with no
 * animation reset, and the existing CSS positioning is reused as-is.
 *
 * To keep the shell visible, the overlay also toggles the `data-loading-start`
 * attribute on `<html>` for the lifetime of its `visible` state: setting it
 * when the overlay first appears (e.g. when navigating from the dashboard
 * into a file, after the dashboard route had already removed it) and
 * removing it when the overlay finally hides (so the shell tears down at the
 * same instant the progress bar disappears, with no in-between frame where
 * the logo is gone but the bar is still showing).
 */
export const FileLoadingOverlay = ({ immediate = false }: Props) => {
  const { status, uuid } = useAtomValue(fileLoadingAtom);
  const progress = useAtomValue(fileLoadingProgressAtom);
  const initialMultiplayerSynced = useAtomValue(initialMultiplayerSyncedAtom);
  const navigation = useNavigation();
  const location = useLocation();

  // Treat navigation to /file/:uuid as loading only if the atom hasn't already
  // finished loading that exact file. Without this check, there's a brief
  // window after `loadFile` resolves where status === 'loaded' but the route
  // loader hasn't returned yet, so navigation.state stays 'loading' — that
  // would otherwise make the overlay flicker back to the indeterminate
  // animation right before unmounting.
  //
  // The regex is intentionally limited to the bare /file/:uuid viewer route;
  // sibling routes like /file/:uuid/duplicate and /file/:uuid/history don't
  // load the spreadsheet and shouldn't gate the overlay.
  const targetUuid = navigation.location?.pathname.match(/^\/file\/([^/?#]+)\/?(?:[?#]|$)/)?.[1] ?? null;
  const isNavigatingToFile =
    navigation.state === 'loading' && targetUuid !== null && !(status === 'loaded' && uuid === targetUuid);

  // Hold the overlay through the post-checkpoint multiplayer replay window
  // so the user never sees the OLD state (e.g. a renamed table flashing its
  // pre-rename name) before the server's catch-up transactions land. We
  // only enforce this gate while we're actually viewing a file, so that
  // navigating to dashboard or other routes during the brief sync window
  // doesn't keep the overlay up over those routes. The gate flips true on
  // the first `multiplayerSynced` event after a load, and is also opened
  // by a safety timeout in `loadFile` so a broken websocket can never
  // strand the overlay.
  const onFileRoute = /^\/file\/[^/?#]+\/?(?:[?#]|$)/.test(location.pathname);
  const isWaitingForInitialSync = onFileRoute && status === 'loaded' && !initialMultiplayerSynced;

  const isLoading = status === 'loading' || isNavigatingToFile || isWaitingForInitialSync;
  // After the atom finishes loading but before React Router commits the new
  // file route, the dashboard route is still mounted underneath. If we
  // unmount the overlay during this window, the dashboard flashes through
  // before the sheet renders. Detect this "post-load, pre-commit" state so
  // the hide path can wait for the commit before tearing down.
  const isCommittingFile =
    navigation.state === 'loading' && targetUuid !== null && status === 'loaded' && uuid === targetUuid;

  // Cold-load path: index.html already shows the GIF backdrop, so we want
  // to take over immediately without an additional 500ms gap.
  const skipDelay =
    immediate || (typeof document !== 'undefined' && document.documentElement.hasAttribute('data-loading-start'));

  const [visible, setVisible] = useState<boolean>(skipDelay && isLoading);
  const [flashComplete, setFlashComplete] = useState<boolean>(false);
  // Refs avoid re-running the effect on every visible/sawDeterminate change
  // (which would otherwise schedule redundant timers).
  const visibleRef = useRef<boolean>(skipDelay && isLoading);
  const sawDeterminateRef = useRef<boolean>(false);
  // When the overlay first became visible, used to enforce MIN_VISIBLE_MS.
  const shownAtRef = useRef<number | null>(skipDelay && isLoading ? Date.now() : null);
  // Wall-clock timestamp of the first byte received from the file fetch.
  // Used to compute streaming throughput for the time-based bar gate.
  // Cleared between loads (when progress goes back to null).
  const fetchStartedAtRef = useRef<number | null>(null);
  // Sticky once we've decided to show the bar in this load. Network rate
  // can fluctuate, so we never take the bar back once it's appeared.
  const showBarStickyRef = useRef<boolean>(false);

  useEffect(() => {
    if (progress === null) {
      fetchStartedAtRef.current = null;
      showBarStickyRef.current = false;
      return;
    }
    if (fetchStartedAtRef.current === null && progress.loaded > 0) {
      fetchStartedAtRef.current = Date.now();
    }
  }, [progress]);

  // Decide whether the progress bar should be shown for this load. Sticky
  // once true; sees a fresh decision once `progress` resets to null between
  // loads (handled by the effect above). Computed purely from inputs here;
  // the sticky persistence is handled by an effect below to keep render pure.
  let shouldShowBar = showBarStickyRef.current;
  if (!shouldShowBar && progress !== null && progress.total !== null && progress.loaded > 0) {
    const startedAt = fetchStartedAtRef.current;
    if (startedAt !== null) {
      const elapsed = Date.now() - startedAt;
      const remaining = progress.total - progress.loaded;
      if (elapsed >= BAR_WARMUP_MS && remaining > 0) {
        const rate = progress.loaded / elapsed;
        if (rate > 0 && remaining / rate >= BAR_MIN_ETA_MS) {
          shouldShowBar = true;
        }
      }
    }
  }

  useEffect(() => {
    if (shouldShowBar) showBarStickyRef.current = true;
  }, [shouldShowBar]);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // Drive the index.html loading shell's lifecycle off our `visible` state.
  // When we first become visible, ensure the attribute is set so the shell
  // (gray logo + animated gif via body:after) is showing. When we hide, tear
  // it down in the same paint as the overlay unmount.
  //
  // The `wasVisibleRef` initial value is also used to clean up a leftover
  // shell on mount: when `immediate` is false (i.e. this is the post-
  // HydrateFallback overlay rendered from `_root.tsx`'s Component), an
  // already-set `data-loading-start` attribute means index.html's shell is
  // still showing from the cold-load handoff. If we're not actively loading,
  // we should tear it down — otherwise the shell is leaked because the
  // HydrateFallback overlay's flash timer was clobbered by react-router
  // unmounting it the moment the loaders resolved. The `immediate=true`
  // (HydrateFallback) path keeps `wasVisibleRef` strictly tied to actual
  // visibility so it doesn't strip the shell out from under non-file routes
  // (e.g. dashboard cold load) while the root route is still loading.
  const wasVisibleRef = useRef<boolean>(
    (skipDelay && isLoading) ||
      (!immediate && typeof document !== 'undefined' && document.documentElement.hasAttribute('data-loading-start'))
  );
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    if (visible && !wasVisibleRef.current) {
      if (!document.documentElement.hasAttribute('data-loading-start')) {
        document.documentElement.setAttribute('data-loading-start', '');
      }
    } else if (!visible && wasVisibleRef.current) {
      document.documentElement.removeAttribute('data-loading-start');
    }
    wasVisibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    if (shouldShowBar && visible) sawDeterminateRef.current = true;
  }, [shouldShowBar, visible]);

  useEffect(() => {
    if (isLoading) {
      setFlashComplete(false);
      if (visibleRef.current) return;
      if (skipDelay) {
        setVisible(true);
        shownAtRef.current = Date.now();
        return;
      }
      const timer = setTimeout(() => {
        setVisible(true);
        shownAtRef.current = Date.now();
      }, SHOW_AFTER_MS);
      return () => clearTimeout(timer);
    }

    if (!visibleRef.current) {
      sawDeterminateRef.current = false;
      shownAtRef.current = null;
      return;
    }

    if (sawDeterminateRef.current) setFlashComplete(true);

    // Hold the overlay up until React Router has actually committed the
    // file route. Otherwise the dashboard underneath flashes through
    // between the overlay unmounting and the sheet mounting. The effect
    // re-runs when `isCommittingFile` flips false (navigation -> idle),
    // at which point the unmount timer below is scheduled.
    if (isCommittingFile) return;

    // Compute remaining min-visible time; the unmount path waits at least
    // until both that and (if applicable) the completion flash have elapsed.
    const elapsed = shownAtRef.current === null ? MIN_VISIBLE_MS : Date.now() - shownAtRef.current;
    const minRemaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    const flashRemaining = sawDeterminateRef.current ? COMPLETION_FLASH_MS : 0;
    const wait = Math.max(minRemaining, flashRemaining);

    const timer = setTimeout(() => {
      setFlashComplete(false);
      setVisible(false);
      sawDeterminateRef.current = false;
      shownAtRef.current = null;
    }, wait);
    return () => clearTimeout(timer);
  }, [isLoading, isCommittingFile, skipDelay]);

  if (!visible) return null;

  // The bar only appears once the throughput-based gate above has decided
  // it'll live long enough to be useful. The GIF is sufficient to convey
  // "loading" during the lazy-chunk / asset / WASM preload phases that
  // come before the file fetch, and during the warmup window itself.
  //
  // During the completion flash we force the bar to 100% so it visibly
  // finishes before unmounting; this also masks cases where Content-Length
  // doesn't match the streamed byte count (e.g. gzip / chunked encoding).
  // The flash is suppressed when a new load has already started (isLoading
  // true again) so we never carry the previous file's "completed" bar into
  // the next load's first render frame.
  const showFlash = flashComplete && !isLoading;
  const showBar = shouldShowBar || showFlash;
  const rawPercent =
    shouldShowBar && progress && progress.total
      ? Math.min(99, Math.floor((progress.loaded / progress.total) * 100))
      : 0;
  const percent = showFlash ? 100 : rawPercent;

  // The logo (gray etching + animated gif) is provided by the index.html
  // shell's `body:after` and painted on top of this overlay (z-index 10000),
  // so we only render the opaque mask + progress bar here. Reusing the same
  // gif element keeps its animation playing smoothly across the
  // cold-load → React-hydrated transition.
  return (
    <div className="fixed inset-0 z-[9999] bg-background" role="status" aria-live="polite" aria-label="Loading file">
      <div className="absolute left-1/2 top-[calc(50%+74px)] h-1 w-64 -translate-x-1/2">
        {showBar && <Progress value={percent} className="h-1" />}
      </div>
    </div>
  );
};
